import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import { HttpUtils } from '../utils/http-utils';
import { Page } from '../domain/page.model';
import {
  DemandPartner,
  DemandPartnerCreate,
  DemandPartnersRequest,
  DemandPartnerStatus,
} from '../domain/demand-partner.model';
import {
  DemandPartnerDomain,
  DemandPartnerDomainFilter,
} from '../domain/demand-partner-domain.model';
import {
  DemandPartnerConnection,
  DemandPartnersConnectionRequest,
} from '../domain/demand-partner-connection.model';
import { ConnectionPlacement, Placements } from '../domain/placements.model';
import { first, tap } from 'rxjs/operators';
import { MixpanelService } from './mixpanel.service';
import { MixpanelEvents } from '../analytics/mixpanel-events';
import { withMixpanelOutcome } from '../analytics/mixpanel-tracking.utils';
import { DpAppCompliance } from '../domain/demand-partner-compliance.model';
import { Discrepancy, DiscrepancyImportHistory } from '../domain/discrepancies.model';

@Injectable({
  providedIn: 'root',
})
export class DemandPartnersService {
  constructor(
    private httpClient: HttpClient,
    private mixpanel: MixpanelService
  ) {}

  list(filter: DemandPartnersRequest): Observable<Page<DemandPartner>> {
    return this.httpClient.get<Page<DemandPartner>>(
      `${environment.serviceUrl}/api/dp`,
      {
        params: HttpUtils.getHttpParams(filter),
      }
    );
  }

  listAll(filter: DemandPartnersRequest): Observable<DemandPartner[]> {
    return this.httpClient.get<DemandPartner[]>(
      `${environment.serviceUrl}/api/dp/all`,
      {
        params: HttpUtils.getHttpParams(filter),
      }
    );
  }

  getDpStatuses(filter: DemandPartnersRequest): Observable<DemandPartnerStatus> {
    return this.httpClient.get<DemandPartnerStatus>(
      `${environment.serviceUrl}/api/dp/statuses`,
      {
        params: HttpUtils.getHttpParams(filter),
      }
    );
  }

  getDpById(id) {
    return this.httpClient.get(`${environment.serviceUrl}/api/dp/${id}`);
  }

  updateDemandPartner(dp: DemandPartnerCreate) {
    return this.httpClient.put(`${environment.serviceUrl}/api/dp/${dp.id}`, dp);
  }

  addDemandPartner(dp: DemandPartnerCreate) {
    return this.httpClient
      .post(`${environment.serviceUrl}/api/dp`, dp)
      .pipe(
        withMixpanelOutcome(this.mixpanel, {
          successEvent: MixpanelEvents.PartnerCreated,
          failureEvent: MixpanelEvents.PartnerCreateFailed,
          successProps: (res: any) => ({
            partner_id: res?.id,
            partner_display_name: res?.displayName ?? dp?.displayName,
          }),
          failureProps: () => ({
            partner_display_name: dp?.displayName,
          }),
        })
      );
  }

  countByStatus() {
    return this.httpClient.get(
      `${environment.serviceUrl}/api/dp/count-by-status`
    );
  }

  // getEmbedInitialDp(id: string): Observable<Page<DemandPartner>> {
  //   return this.httpClient.get<Page<DemandPartner>>(
  //     `${environment.serviceUrl}/api/dp/domain/${id}`
  //   );
  // }

  // getDemandPartnerProfile(
  //   filter: DemandPartnerDomainFilter
  // ): Observable<Page<DemandPartnerDomain>> {
  //   return this.httpClient.get<Page<DemandPartnerDomain>>(
  //     `${environment.serviceUrl}/api/dp/profile`,
  //     {
  //       params: HttpUtils.getHttpParams(filter),
  //     }
  //   );
  // }

  getDemandPartnerBundleIds(
    filter: DemandPartnerDomainFilter,
    id: string
  ): Observable<Page<DemandPartnerDomain>> {
    return this.httpClient
      .post<Page<DemandPartnerDomain>>(
        `${environment.serviceUrl}/api/dp/approvals/${id}`,
        filter
      )
      .pipe(first());
  }

  getDemandPartnerDomains(filter: DemandPartnerDomainFilter, id: string): Observable<Page<DemandPartnerDomain>> {
    return this.httpClient.get<Page<DemandPartnerDomain>>(`${environment.serviceUrl}/api/dp/domain-approvals/${id}`, {
        params: HttpUtils.getHttpParams(filter)
      });
  }


  dpProfileCountByStatus(name: string) {
    return this.httpClient.get(
      `${environment.serviceUrl}/api/dp/profile/count-by-status`,
      {
        params: HttpUtils.getHttpParams({ name }),
      }
    );
  }

  exportDemandPartnerProfileCsv(name: string) {
    return this.httpClient.get(`${environment.serviceUrl}/api/dp/export`, {
      responseType: 'blob',
      params: HttpUtils.getHttpParams({ name }),
    });
  }

  exportDpAppsApprovalsCsv(dpId: string, request: any) {
    return this.httpClient.post(
      `${environment.serviceUrl}/api/compliance/dp/${dpId}/approvals/csv/export`,
      request, { responseType: 'blob' });
  }

  importDpCsv(file: File, name: string) {
    const formData: FormData = new FormData();
    formData.append('excelFile', file, file.name);

    return this.httpClient.post(
      `${environment.serviceUrl}/api/dp/import/${name}`,
      formData
    );
  }

  importDpAppsApprovalsCsv(file: File, dpId: string) {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);

    return this.httpClient.post(
      `${environment.serviceUrl}/api/compliance/dp/${dpId}/approvals/csv/import`,
      formData
    );
  }

  getDpConnections(
    filter: DemandPartnersConnectionRequest,
    id: string
  ): Observable<Page<DemandPartnerConnection>> {
    return this.httpClient.get<Page<DemandPartnerConnection>>(
      `${environment.serviceUrl}/api/dp/${id}/connection`,
      {
        params: HttpUtils.getHttpParams(filter),
      }
    );
  }

  getAllConnections(
    filter: DemandPartnersConnectionRequest
  ): Observable<Page<DemandPartnerConnection>> {
    return this.httpClient
      .get<Page<DemandPartnerConnection>>(
        `${environment.serviceUrl}/api/dpconnection`,
        {
          params: HttpUtils.getHttpParams(filter),
        }
      )
      .pipe(first());
  }

  createConnection(request: DemandPartnerConnection, dpId: string) {
    return this.httpClient
      .post(`${environment.serviceUrl}/api/dp/${dpId}/connection`, request)
      .pipe(
        withMixpanelOutcome(this.mixpanel, {
          successEvent: MixpanelEvents.ConnectionCreated,
          failureEvent: MixpanelEvents.ConnectionCreateFailed,
          successProps: (res: any) => ({
            partner_id: dpId,
            connection_id: res?.id,
            connection_name: request?.name,
          }),
          failureProps: () => ({
            partner_id: dpId,
            connection_name: request?.name,
          }),
        })
      );
  }

  editConnection(
    request: DemandPartnerConnection,
    dpId: string,
    conId: string
  ) {
    return this.httpClient.put(
      `${environment.serviceUrl}/api/dp/${dpId}/connection/${conId}`,
      request
    );
  }

  getConnectionById(
    dpId: string,
    conId: string
  ): Observable<DemandPartnerConnection> {
    return this.httpClient.get<DemandPartnerConnection>(
      `${environment.serviceUrl}/api/dp/${dpId}/connection/${conId}`
    );
  }

  getPlacementByConnection(
    dpId: string,
    connectionId: string,
    filter: any
  ): Observable<Page<Placements>> {
    return this.httpClient.get<Page<Placements>>(
      `${environment.serviceUrl}/api/dp/${dpId}/connection/${connectionId}/placements`,
      {
        params: HttpUtils.getHttpParams(filter),
      }
    );
  }

  getStatusCount(filter?) {
    return this.httpClient.get(
      `${environment.serviceUrl}/api/dpconnection/statuses`,
      {
        params: HttpUtils.getHttpParams(filter),
      }
    );
  }

  getAdsTxtRecordsByDPId(dpId: string): Observable<DemandPartner> {
    return this.httpClient.get<DemandPartner>(`${environment.serviceUrl}/api/dp/${dpId}`);
  }

  //DP App Approvalls
  public getAppsApprovalsByDpID(filter: any, dpId: string): Observable<Page<DpAppCompliance>> {
    // return this.httpClient.get<Page<any>>(`${environment.serviceUrl}/api/publisher-apps`, {
    return this.httpClient.get<Page<DpAppCompliance>>(`${environment.serviceUrl}/api/compliance/dp/${dpId}`, {
      params: HttpUtils.getHttpParams(filter)
    });
  }

  public getAppsApprovalsStatusCount(filter: any, dpId: string) {
    return this.httpClient.get(
      `${environment.serviceUrl}/api/compliance/dp/${dpId}/count-apps-by-status`,
      {
        params: HttpUtils.getHttpParams(filter),
      }
    );
  }

  //Discrepancies
  public getImportStatusHistory(filter: any): Observable<DiscrepancyImportHistory> {
    return this.httpClient.get<DiscrepancyImportHistory>(
      `${environment.serviceUrl}/api/dp/dp-revenue/imports`,
      {
        params: HttpUtils.getHttpParams(filter),
      }
    );
  }

  public getDiscrepancies(filter: any): Observable<Discrepancy[]> {
    return this.httpClient.post<Discrepancy[]>(
      `${environment.serviceUrl}/api/report/discrepancy`, filter);
  }

  public getSupportedImportDPs(): Observable<any[]> {
    return this.httpClient.get<any[]>(
      `${environment.serviceUrl}/api/dp/dp-revenue/supported-imports`
    );
  }

  public importDiscrepanciesCsv(file: File, dpId: string): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    const url = `${environment.serviceUrl}/api/dp/${dpId}/dp-revenue/csv/import`;
    return this.httpClient.post(url, formData);
  }
}
